import { GraphqlClient } from 'src/api/graphql_client';
import {
  OrderBaseFieldsFragment,
  DeleteOrderDocument,
  CancelMultipartUploadMutation,
  CancelMultipartUploadDocument,
  ActivityLogQuery,
  ActivityLogDocument,
  CancelOrderDocument,
  CancelOrderMutation,
} from 'src/types';
import { ActivityResourceType } from 'src/types/activtiy_logs';

/**
 * Base class for the implementation of the Order Repository for a GraphQL backend.
 * `TOrder` matches the platform repository's order fragment type.
 */
export abstract class GraphqlOrderRepository<
  TOrder extends OrderBaseFieldsFragment = OrderBaseFieldsFragment,
> {
  constructor(protected readonly _graphqlClient: GraphqlClient) {}

  // Concrete implementations of the interface methods.
  async cancel(order: TOrder): Promise<TOrder> {
    const response = await this._graphqlClient.request<CancelOrderMutation>(
      CancelOrderDocument,
      {
        id: order.id,
      },
    );

    return {
      ...order,
      ...response.cancelOrder?.order,
    };
  }

  async deleteOrder(id: string): Promise<void> {
    await this._graphqlClient.request(DeleteOrderDocument, { id });
  }

  async cancelUpload(fileId: string): Promise<void> {
    await this._graphqlClient.request<CancelMultipartUploadMutation>(
      CancelMultipartUploadDocument,
      {
        input: {
          uploadedFileId: fileId,
        },
      },
    );
  }

  // This method does not exist for all platforms, so we implement it as a no-op here.
  // For the platforms that do use it, it will be overridden in the concrete implementation.
  async deleteFile(_: string): Promise<void> {}

  async getActivityLogs(orderId: string): Promise<ActivityLogQuery> {
    const response = await this._graphqlClient.request<ActivityLogQuery>(
      ActivityLogDocument,
      {
        resourceId: orderId,
        resourceType: ActivityResourceType.ORDER,
      },
    );

    return response;
  }
}
